import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useParams } from '@tanstack/react-router';
import { ArrowDown, ArrowUp, Clock, GitBranch, Mail, Trash2 } from 'lucide-react';
import { Badge, Button, EmptyState, Field, Input, Section, Select, Spinner, Switch } from '@zt/ui';
import { ConfirmButton } from '../components/Confirm';
import { reviewTone } from '../components/ContentReview';
import { api } from '../lib/api';
import { HelpTip, PageFrame } from '../lib/help';
import { useI18n, useT } from '../lib/i18n';
import { useMoney } from '../lib/money';
import { fieldError, useApiMutation } from '../lib/mutation';
import { usePermissions } from '../lib/session';
import type {
  AutomationDetail,
  AutomationReport,
  AutomationRow,
  AutomationSettings,
  AutomationStep,
  EmailTemplateRow,
} from '../lib/types';
import { StorePicker, useStoreChoice } from './payments';

/**
 * Pazarlama → Otomasyonlar (F6-07, ADR-088): 9 hazır akış (varsayılan kapalı), işletme geneli sıklık sınırı ve
 * sessiz saat; akış düzenleyici (mağaza, tetikleyici süresi, bekle / koşul / e-posta adımları) ve rapor.
 */

type Kind = AutomationRow['kind'];

/** Panel adresindeki Türkçe kısa ad ↔ akış türü. */
export const AUTOMATION_SLUGS: Record<Kind, string> = {
  welcome: 'hos-geldin',
  cart_abandon: 'sepet-terk',
  checkout_abandon: 'odeme-bekleyen',
  post_purchase: 'satin-alma-sonrasi',
  repurchase: 'tekrar-satin-alma',
  winback: 'geri-kazanma',
  vip: 'vip',
  birthday: 'dogum-gunu',
  dealer_welcome: 'bayi-karsilama',
  quote_followup: 'teklif-takibi',
};
const kindOfSlug = (slug: string) =>
  (Object.entries(AUTOMATION_SLUGS) as Array<[Kind, string]>).find(([, s]) => s === slug)?.[0];

const CAP_OPTIONS = [0, 12, 24, 48, 72, 168];
const HOURS = Array.from({ length: 24 }, (_, i) => i);
const AFTER_HOURS = [1, 2, 3, 4, 6, 12, 24, 48, 72];
const MAX_STEPS = 10;
const SKIP_KEYS = [
  'suppressed',
  'no_consent',
  'iys_not_registered',
  'iys_refused',
  'invalid_address',
  'frequency_cap',
  'template_unavailable',
  'staging_allowlist',
  'rejected',
  'error',
] as const;
const EXIT_KEYS = [
  'cart_not_converted',
  'order_not_paid',
  'not_reviewed',
  'no_order_since_enrollment',
  'superseded',
  'expired',
  'flow_changed',
  'disabled',
] as const;

const pad = (h: number) => `${String(h).padStart(2, '0')}:00`;

// ---------------------------------------------------------------------------- Liste ve ayarlar

export function AutomationsPage() {
  const t = useT();
  const list = useQuery({
    queryKey: ['automations'],
    queryFn: () => api.get<AutomationRow[]>('/v1/admin/automations'),
  });
  return (
    <PageFrame helpKey="page.automations">
      <div className="space-y-8">
        <p className="max-w-[70ch] text-sm text-ink-2">{t('admin.automations.lead')}</p>
        {!list.data ? (
          <Spinner label={t('common.loading')} />
        ) : (
          <div className="overflow-x-auto rounded-md border border-line">
            <table className="w-full text-sm" aria-label={t('admin.automations.listLabel')}>
              <thead className="bg-surface-2 text-ink-2">
                <tr>
                  <th className="px-3 py-2 text-start font-medium">{t('admin.automations.colFlow')}</th>
                  <th className="px-3 py-2 text-start font-medium">{t('admin.automations.colStatus')}</th>
                  <th className="px-3 py-2 text-end font-medium">{t('admin.automations.colEnrolled')}</th>
                  <th className="px-3 py-2 text-end font-medium">{t('admin.automations.colSent')}</th>
                </tr>
              </thead>
              <tbody>
                {list.data.map((a) => (
                  <tr key={a.kind} className="border-t border-line align-top">
                    <td className="px-3 py-2">
                      <Link
                        to="/otomasyonlar/$kind"
                        params={{ kind: AUTOMATION_SLUGS[a.kind] }}
                        className="font-medium text-pine hover:underline"
                      >
                        {t(`admin.automations.kinds.${a.kind}`)}
                      </Link>
                      <p className="max-w-[60ch] text-ink-3">{t(`admin.automations.summaries.${a.kind}`)}</p>
                    </td>
                    <td className="px-3 py-2">
                      <Badge tone={a.enabled ? 'moss' : 'neutral'}>
                        {a.enabled ? t('admin.automations.on') : t('admin.automations.off')}
                      </Badge>
                    </td>
                    <td className="px-3 py-2 text-end tabular-nums">
                      {a.last30.enrolled}
                      {a.last30.active > 0 && (
                        <span className="block text-ink-3">
                          {t('admin.automations.activeCount', { count: a.last30.active })}
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-end tabular-nums">{a.last30.sent}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <p className="text-sm text-ink-3">{t('admin.automations.last30Note')}</p>
        <SettingsSection />
      </div>
    </PageFrame>
  );
}

function SettingsSection() {
  const t = useT();
  const { can } = usePermissions();
  const canManage = can('automation.manage');
  const q = useQuery({
    queryKey: ['automation-settings'],
    queryFn: () => api.get<AutomationSettings>('/v1/admin/automation-settings'),
  });
  const [draft, setDraft] = useState<AutomationSettings | null>(null);
  useEffect(() => {
    if (q.data) setDraft(q.data);
  }, [q.data]);
  const save = useApiMutation(() => api.put<AutomationSettings>('/v1/admin/automation-settings', draft), {
    invalidate: [['automation-settings']],
    success: t('admin.automations.settingsSaved'),
  });
  if (!draft) return <Spinner label={t('common.loading')} />;
  const dirty = JSON.stringify(draft) !== JSON.stringify(q.data);
  const quiet = draft.quietHours;
  return (
    <Section
      title={t('admin.automations.settingsTitle')}
      description={t('admin.automations.settingsLead')}
      action={<HelpTip k="automations.settings" />}
    >
      <fieldset disabled={!canManage} className="grid max-w-2xl gap-4">
        <legend className="sr-only">{t('admin.automations.settingsTitle')}</legend>
        <Field
          label={t('admin.automations.frequencyCap')}
          description={t('admin.automations.frequencyCapLead')}
          help={<HelpTip k="automations.frequency_cap" />}
          className="max-w-sm"
        >
          {(p) => (
            <Select
              {...p}
              value={draft.frequencyCapHours}
              onChange={(e) => setDraft({ ...draft, frequencyCapHours: Number(e.target.value) })}
            >
              {CAP_OPTIONS.map((h) => (
                <option key={h} value={h}>
                  {h === 0 ? t('admin.automations.capNone') : t('admin.automations.capHours', { count: h })}
                </option>
              ))}
            </Select>
          )}
        </Field>
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <Switch
              id="automation-quiet"
              checked={!!quiet}
              onCheckedChange={(v) => setDraft({ ...draft, quietHours: v ? { start: 21, end: 9 } : null })}
              disabled={!canManage}
            />
            <label htmlFor="automation-quiet" className="text-sm font-medium">
              {t('admin.automations.quietHours')}
            </label>
            <HelpTip k="automations.quiet_hours" />
          </div>
          {quiet && (
            <div className="grid max-w-sm grid-cols-2 gap-3">
              <Field label={t('admin.automations.quietStart')}>
                {(p) => (
                  <Select
                    {...p}
                    value={quiet.start}
                    onChange={(e) =>
                      setDraft({ ...draft, quietHours: { ...quiet, start: Number(e.target.value) } })
                    }
                  >
                    {HOURS.map((h) => (
                      <option key={h} value={h}>
                        {pad(h)}
                      </option>
                    ))}
                  </Select>
                )}
              </Field>
              <Field label={t('admin.automations.quietEnd')}>
                {(p) => (
                  <Select
                    {...p}
                    value={quiet.end}
                    onChange={(e) =>
                      setDraft({ ...draft, quietHours: { ...quiet, end: Number(e.target.value) } })
                    }
                  >
                    {HOURS.map((h) => (
                      <option key={h} value={h}>
                        {pad(h)}
                      </option>
                    ))}
                  </Select>
                )}
              </Field>
            </div>
          )}
          <p className="text-sm text-ink-3">
            {quiet
              ? t('admin.automations.quietLead', { start: pad(quiet.start), end: pad(quiet.end) })
              : t('admin.automations.quietOff')}
          </p>
        </div>
        {canManage && (
          <div className="flex items-center gap-2">
            <Button onClick={() => save.mutate(undefined)} disabled={!dirty || save.isPending}>
              {t('admin.automations.saveSettings')}
            </Button>
            <HelpTip k="automations.settings_save" />
          </div>
        )}
      </fieldset>
    </Section>
  );
}

// ---------------------------------------------------------------------------- Akış düzenleyici

type DraftStep = AutomationStep & { _k: number };
interface Draft {
  storeId: string;
  enabled: boolean;
  steps: DraftStep[];
  afterHours: number | null;
  days: number | null;
}

let stepKey = 0;
const withKeys = (steps: AutomationStep[]): DraftStep[] => steps.map((s) => ({ ...s, _k: ++stepKey }));
const plainSteps = (steps: DraftStep[]): AutomationStep[] =>
  steps.map(({ _k, ...s }) => {
    void _k;
    return s as AutomationStep;
  });
const draftOf = (a: AutomationDetail, storeId: string): Draft => ({
  storeId: a.storeId ?? storeId,
  enabled: a.enabled,
  steps: withKeys(a.steps),
  afterHours: a.params.afterHours ?? null,
  days: a.params.days ?? null,
});
const comparable = (d: Draft) => JSON.stringify({ ...d, steps: plainSteps(d.steps) });

export function AutomationEditorPage() {
  const { kind: slug } = useParams({ from: '/app/otomasyonlar/$kind' });
  const t = useT();
  const kind = kindOfSlug(slug);
  const q = useQuery({
    queryKey: ['automation', kind],
    queryFn: () => api.get<AutomationDetail>(`/v1/admin/automations/${kind}`),
    enabled: !!kind,
  });
  if (!kind)
    return (
      <PageFrame helpKey="page.automation_editor">
        <EmptyState
          title={t('admin.automations.unknownTitle')}
          description={t('admin.automations.unknownLead')}
        />
      </PageFrame>
    );
  if (!q.data) return <Spinner label={t('common.loading')} />;
  return <AutomationEditor key={`${q.data.kind}-${q.data.version}`} automation={q.data} />;
}

function AutomationEditor({ automation: a }: { automation: AutomationDetail }) {
  const t = useT();
  const { formatDate } = useI18n();
  const { can } = usePermissions();
  const canManage = can('automation.manage');
  const { stores, storeId: chosenStore, setStoreId } = useStoreChoice();
  const saved = draftOf(a, chosenStore);
  const [draft, setDraft] = useState<Draft>(saved);
  const set = (patch: Partial<Draft>) => setDraft((d) => ({ ...d, ...patch }));
  // Mağaza listesi sonradan yüklenirse kaydı olmayan akışta seçili mağaza kullanılır.
  useEffect(() => {
    if (!draft.storeId && chosenStore) set({ storeId: chosenStore });
  }, [chosenStore, draft.storeId]);
  const dirty = comparable(draft) !== comparable(saved);
  const stepsChanged = JSON.stringify(plainSteps(draft.steps)) !== JSON.stringify(a.steps);
  const templates = useQuery({
    queryKey: ['email-templates'],
    queryFn: () => api.get<EmailTemplateRow[]>('/v1/admin/email-templates'),
  });
  const storeTemplates = (templates.data ?? []).filter((x) => x.storeId === draft.storeId);
  const turningOn = draft.enabled && !a.enabled;
  const [announce, setAnnounce] = useState('');

  const body = () => ({
    storeId: draft.storeId,
    enabled: draft.enabled,
    steps: plainSteps(draft.steps),
    params: {
      ...(draft.afterHours !== null ? { afterHours: draft.afterHours } : {}),
      ...(draft.days !== null ? { days: draft.days } : {}),
    },
    ...(a.id ? { version: a.version } : {}),
  });
  const save = useApiMutation(() => api.put<AutomationDetail>(`/v1/admin/automations/${a.kind}`, body()), {
    invalidate: [['automations'], ['automation', a.kind], ['automation-report', a.kind]],
    success: turningOn
      ? t('admin.automations.enabledToast')
      : draft.enabled || !a.enabled
        ? t('admin.automations.saved')
        : t('admin.automations.disabledToast'),
  });
  const fe = (f: string) => fieldError(save.error, f);

  const setStep = (i: number, s: AutomationStep) =>
    set({ steps: draft.steps.map((x, j) => (j === i ? { ...s, _k: x._k } : x)) });
  const move = (i: number, to: number) => {
    const steps = [...draft.steps];
    const [s] = steps.splice(i, 1);
    steps.splice(to, 0, s!);
    set({ steps });
    setAnnounce(t('admin.automations.moved', { n: to + 1 }));
  };
  const add = (s: AutomationStep) => {
    set({ steps: [...draft.steps, ...withKeys([s])] });
    setAnnounce(t('admin.automations.added', { n: draft.steps.length + 1 }));
  };
  const saveButton = turningOn ? (
    <ConfirmButton
      variant="primary"
      size="md"
      message={t('admin.automations.enableConfirm')}
      // Hata olursa pencere kapanır; neden bildirimde ve ilgili adımın altında görünür.
      onConfirm={() => save.mutateAsync(undefined).catch(() => undefined)}
      disabled={!dirty || !draft.storeId || save.isPending}
    >
      {t('admin.automations.saveAndEnable')}
    </ConfirmButton>
  ) : (
    <Button
      variant="primary"
      onClick={() => save.mutate(undefined)}
      disabled={!dirty || !draft.storeId || save.isPending}
    >
      {t('admin.automations.save')}
    </Button>
  );

  return (
    <PageFrame
      helpKey="page.automation_editor"
      title={t(`admin.automations.kinds.${a.kind}`)}
      actions={
        canManage && (
          <div className="flex items-center gap-2">
            {saveButton}
            <HelpTip k="automations.save" />
          </div>
        )
      }
    >
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <Link to="/otomasyonlar" className="text-pine hover:underline">
          {t('admin.automations.back')}
        </Link>
        <Badge tone={a.enabled ? 'moss' : 'neutral'}>
          {a.enabled ? t('admin.automations.on') : t('admin.automations.off')}
        </Badge>
        {dirty && <Badge tone="amber">{t('admin.automations.unsaved')}</Badge>}
      </div>
      <p className="sr-only" aria-live="polite">
        {announce}
      </p>

      <fieldset disabled={!canManage} className="space-y-8">
        <legend className="sr-only">{t('admin.automations.editor')}</legend>
        <Section title={t('admin.automations.statusTitle')} className="border-t-0 pt-0">
          <div className="max-w-2xl space-y-3">
            <div className="flex items-center gap-2">
              <Switch
                id="automation-enabled"
                checked={draft.enabled}
                onCheckedChange={(v) => set({ enabled: v })}
                disabled={!canManage}
              />
              <label htmlFor="automation-enabled" className="text-sm font-medium">
                {t('admin.automations.enabledLabel')}
              </label>
              <HelpTip k="automations.enabled" />
            </div>
            <p className="text-sm text-ink-2">
              {a.enabled && a.enabledAt
                ? t('admin.automations.enabledSince', { date: formatDate(a.enabledAt) })
                : t('admin.automations.enableLead')}
            </p>
            {a.enabled && !draft.enabled && (
              <p role="status" className="text-sm text-amber">
                {t('admin.automations.disableWarning')}
              </p>
            )}
            {a.enabled && draft.enabled && stepsChanged && (
              <p role="status" className="text-sm text-amber">
                {t('admin.automations.stepsChangedWarning')}
              </p>
            )}
          </div>
        </Section>

        <Section
          title={t('admin.automations.triggerTitle')}
          description={t(`admin.automations.triggers.${a.kind}`, {
            hours: draft.afterHours ?? 0,
            days: draft.days ?? 0,
          })}
          action={<HelpTip k="automations.trigger" />}
        >
          <div className="grid max-w-2xl gap-4">
            {stores.length > 1 && (
              <div className="flex items-end gap-2">
                <StorePicker
                  stores={stores}
                  storeId={draft.storeId}
                  onChange={(id) => {
                    setStoreId(id);
                    set({ storeId: id });
                  }}
                />
                <HelpTip k="automations.store" />
              </div>
            )}
            {draft.afterHours !== null && (
              <Field
                label={t('admin.automations.afterHours')}
                help={<HelpTip k="automations.after_hours" />}
                error={fe('params.afterHours')}
                className="max-w-xs"
              >
                {(p) => (
                  <Select
                    {...p}
                    value={draft.afterHours ?? 1}
                    onChange={(e) => set({ afterHours: Number(e.target.value) })}
                  >
                    {AFTER_HOURS.map((h) => (
                      <option key={h} value={h}>
                        {t('admin.automations.hoursOption', { count: h })}
                      </option>
                    ))}
                  </Select>
                )}
              </Field>
            )}
            {draft.days !== null && (
              <Field
                label={t(`admin.automations.daysLabel.${a.kind}`)}
                help={<HelpTip k="automations.days" />}
                error={fe('params.days')}
                className="max-w-xs"
              >
                {(p) => (
                  <Input
                    {...p}
                    type="number"
                    min={1}
                    max={365}
                    value={draft.days ?? 1}
                    onChange={(e) => set({ days: Math.max(1, Math.min(365, Number(e.target.value) || 1)) })}
                  />
                )}
              </Field>
            )}
            {!a.customerBased && <p className="text-sm text-ink-3">{t('admin.automations.storeScoped')}</p>}
          </div>
        </Section>

        <Section
          title={t('admin.automations.stepsTitle')}
          description={t('admin.automations.stepsLead')}
          action={<HelpTip k="automations.steps" />}
        >
          <div className="max-w-3xl space-y-3">
            {fe('steps') && (
              <p role="alert" className="text-sm font-medium text-rose">
                {fe('steps')}
              </p>
            )}
            <ol className="space-y-2">
              {draft.steps.map((s, i) => (
                <li key={s._k} className="rounded-md border border-line bg-surface">
                  <div className="flex flex-wrap items-center gap-2 border-b border-line bg-surface-2 px-3 py-1.5">
                    {s.type === 'wait' ? (
                      <Clock className="h-4 w-4 text-ink-3" aria-hidden />
                    ) : s.type === 'condition' ? (
                      <GitBranch className="h-4 w-4 text-ink-3" aria-hidden />
                    ) : (
                      <Mail className="h-4 w-4 text-ink-3" aria-hidden />
                    )}
                    <span id={`step-${s._k}`} className="text-sm font-medium">
                      {t('admin.automations.stepTitle', {
                        n: i + 1,
                        type: t(`admin.automations.stepTypes.${s.type}`),
                      })}
                    </span>
                    {canManage && (
                      <span className="ms-auto flex items-center gap-0.5">
                        <Button
                          size="sm"
                          variant="ghost"
                          disabled={i === 0}
                          onClick={() => move(i, i - 1)}
                          aria-label={t('admin.automations.moveUp', { n: i + 1 })}
                        >
                          <ArrowUp className="h-4 w-4" aria-hidden />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          disabled={i === draft.steps.length - 1}
                          onClick={() => move(i, i + 1)}
                          aria-label={t('admin.automations.moveDown', { n: i + 1 })}
                        >
                          <ArrowDown className="h-4 w-4" aria-hidden />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          disabled={draft.steps.length === 1}
                          onClick={() => {
                            set({ steps: draft.steps.filter((_, j) => j !== i) });
                            setAnnounce(t('admin.automations.removed', { n: i + 1 }));
                          }}
                          aria-label={t('admin.automations.removeStep', { n: i + 1 })}
                        >
                          <Trash2 className="h-4 w-4" aria-hidden />
                        </Button>
                        {i === 0 && <HelpTip k="automations.step_tools" />}
                      </span>
                    )}
                  </div>
                  <div role="group" aria-labelledby={`step-${s._k}`} className="p-3">
                    <StepFields
                      step={s}
                      kind={a.kind}
                      allowed={a.allowedConditions}
                      templates={storeTemplates}
                      onChange={(ns) => setStep(i, ns)}
                      error={(f) => fe(`steps.${i}.${f}`)}
                    />
                  </div>
                </li>
              ))}
            </ol>
            {canManage && draft.steps.length < MAX_STEPS && (
              <div className="flex flex-wrap items-center gap-2 rounded-md border border-dashed border-line-strong p-3">
                <span className="text-sm font-medium">{t('admin.automations.addStep')}</span>
                <Button size="sm" onClick={() => add({ type: 'wait', hours: 24 })}>
                  <Clock className="h-4 w-4" aria-hidden />
                  {t('admin.automations.stepTypes.wait')}
                </Button>
                {a.allowedConditions.length > 0 && (
                  <Button
                    size="sm"
                    onClick={() => add({ type: 'condition', condition: a.allowedConditions[0]! })}
                  >
                    <GitBranch className="h-4 w-4" aria-hidden />
                    {t('admin.automations.stepTypes.condition')}
                  </Button>
                )}
                <Button size="sm" onClick={() => add({ type: 'email', templateId: null })}>
                  <Mail className="h-4 w-4" aria-hidden />
                  {t('admin.automations.stepTypes.email')}
                </Button>
                <HelpTip k="automations.add_step" />
              </div>
            )}
            {canManage && (
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() =>
                    set({
                      steps: withKeys(a.defaults.steps),
                      afterHours: a.defaults.params.afterHours ?? null,
                      days: a.defaults.params.days ?? null,
                    })
                  }
                >
                  {t('admin.automations.resetDefaults')}
                </Button>
                <HelpTip k="automations.reset" />
              </div>
            )}
            {!storeTemplates.length && templates.data && (
              <p className="text-sm text-ink-2">
                {t('admin.automations.noTemplates')}{' '}
                <Link to="/e-posta-sablonlari" className="text-pine hover:underline">
                  {t('admin.nav.emailTemplates')}
                </Link>
              </p>
            )}
          </div>
        </Section>
      </fieldset>

      {a.id && <ReportSection kind={a.kind} />}
      {a.updatedAt && (
        <p className="text-sm text-ink-3">
          {t('admin.automations.updated', { date: formatDate(a.updatedAt) })}
        </p>
      )}
    </PageFrame>
  );
}

function StepFields({
  step,
  kind,
  allowed,
  templates,
  onChange,
  error,
}: {
  step: AutomationStep;
  kind: Kind;
  allowed: AutomationDetail['allowedConditions'];
  templates: EmailTemplateRow[];
  onChange: (s: AutomationStep) => void;
  error: (field: string) => string | undefined;
}) {
  const t = useT();
  if (step.type === 'wait')
    return (
      <Field label={t('admin.automations.waitHours')} error={error('hours')} className="max-w-xs">
        {(p) => (
          <Input
            {...p}
            type="number"
            min={1}
            max={720}
            value={step.hours}
            onChange={(e) =>
              onChange({ type: 'wait', hours: Math.max(1, Math.min(720, Number(e.target.value) || 1)) })
            }
          />
        )}
      </Field>
    );
  if (step.type === 'condition')
    return (
      <Field
        label={t('admin.automations.condition')}
        description={t('admin.automations.conditionLead')}
        error={error('condition')}
        className="max-w-md"
      >
        {(p) => (
          <Select
            {...p}
            value={step.condition}
            onChange={(e) =>
              onChange({ type: 'condition', condition: e.target.value as typeof step.condition })
            }
          >
            {(allowed.includes(step.condition) ? allowed : [step.condition, ...allowed]).map((c) => (
              <option key={c} value={c}>
                {t(`admin.automations.conditions.${c}`)}
              </option>
            ))}
          </Select>
        )}
      </Field>
    );
  const chosen = templates.find((x) => x.id === step.templateId);
  return (
    <div className="space-y-2">
      <Field
        label={t('admin.automations.template')}
        description={t(`admin.automations.templateHints.${kind}`)}
        error={error('templateId')}
        className="max-w-md"
      >
        {(p) => (
          <Select
            {...p}
            value={step.templateId ?? ''}
            onChange={(e) => onChange({ type: 'email', templateId: e.target.value || null })}
          >
            <option value="">{t('admin.automations.chooseTemplate')}</option>
            {templates.map((x) => (
              <option key={x.id} value={x.id}>
                {`${x.name} (${x.locale.toUpperCase()})`}
              </option>
            ))}
          </Select>
        )}
      </Field>
      {chosen && (
        <p className="flex flex-wrap items-center gap-2 text-sm">
          {(chosen.reviewRequired || chosen.reviewStatus !== 'not_required') && (
            <Badge tone={reviewTone[chosen.reviewStatus]}>
              {t(`admin.pages.reviews.${chosen.reviewStatus}`)}
            </Badge>
          )}
          <Link to="/e-posta-sablonlari/$id" params={{ id: chosen.id }} className="text-pine hover:underline">
            {t('admin.automations.openTemplate')}
          </Link>
        </p>
      )}
    </div>
  );
}

function ReportSection({ kind }: { kind: Kind }) {
  const t = useT();
  const money = useMoney();
  const q = useQuery({
    queryKey: ['automation-report', kind],
    queryFn: () => api.get<AutomationReport>(`/v1/admin/automations/${kind}/report`),
  });
  const r = q.data;
  if (!r) return <Spinner label={t('common.loading')} />;
  const n = (m: Record<string, number>, k: string) => m[k] ?? 0;
  const enrolled = Object.values(r.runs).reduce((s, x) => s + x, 0);
  const stats: Array<[string, number]> = [
    ['enrolled', enrolled],
    ['active', n(r.runs, 'active')],
    ['completed', n(r.runs, 'completed')],
    ['exited', n(r.runs, 'exited')],
    ['sent', n(r.sends, 'sent')],
    ['skipped', n(r.sends, 'skipped')],
    ['failed', n(r.sends, 'failed')],
  ];
  const exits = EXIT_KEYS.filter((k) => n(r.exits, k) > 0);
  const skips = SKIP_KEYS.filter((k) => n(r.skips, k) > 0);
  const otherSkips = Object.entries(r.skips)
    .filter(([k]) => !(SKIP_KEYS as readonly string[]).includes(k))
    .reduce((s, [, v]) => s + v, 0);
  return (
    <Section
      title={t('admin.automations.reportTitle')}
      description={t('admin.automations.reportLead')}
      action={<HelpTip k="automations.report" />}
    >
      <div className="space-y-6 text-sm">
        <dl className="grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-7">
          {stats.map(([k, v]) => (
            <div key={k} className="rounded-md border border-line bg-surface px-3 py-2">
              <dt className="text-ink-3">{t(`admin.automations.stats.${k}`)}</dt>
              <dd className="font-cond text-xl font-semibold tabular-nums">{v}</dd>
            </div>
          ))}
        </dl>
        {exits.length > 0 && (
          <div>
            <p className="mb-1 font-medium">{t('admin.automations.exitsTitle')}</p>
            <ul className="list-disc space-y-0.5 ps-5 text-ink-2">
              {exits.map((k) => (
                <li key={k}>{t(`admin.automations.exitReasons.${k}`, { count: n(r.exits, k) })}</li>
              ))}
            </ul>
          </div>
        )}
        {(skips.length > 0 || otherSkips > 0) && (
          <div>
            <p className="mb-1 font-medium">{t('admin.automations.skipsTitle')}</p>
            <ul className="list-disc space-y-0.5 ps-5 text-ink-2">
              {skips.map((k) => (
                <li key={k}>{t(`admin.automations.skipReasons.${k}`, { count: n(r.skips, k) })}</li>
              ))}
              {otherSkips > 0 && <li>{t('admin.automations.skipOther', { count: otherSkips })}</li>}
            </ul>
          </div>
        )}
        {r.byStep.length > 0 && (
          <div className="overflow-x-auto rounded-md border border-line">
            <table className="w-full" aria-label={t('admin.automations.byStepTitle')}>
              <thead className="bg-surface-2 text-ink-2">
                <tr>
                  <th className="px-3 py-2 text-start font-medium">{t('admin.automations.colStep')}</th>
                  <th className="px-3 py-2 text-end font-medium">{t('admin.automations.stats.sent')}</th>
                  <th className="px-3 py-2 text-end font-medium">{t('admin.automations.stats.skipped')}</th>
                  <th className="px-3 py-2 text-end font-medium">{t('admin.automations.stats.failed')}</th>
                </tr>
              </thead>
              <tbody>
                {r.byStep.map((s) => (
                  <tr key={s.step} className="border-t border-line">
                    <td className="px-3 py-2">{t('admin.automations.stepNumber', { n: s.step + 1 })}</td>
                    <td className="px-3 py-2 text-end tabular-nums">{s.sent}</td>
                    <td className="px-3 py-2 text-end tabular-nums">{s.skipped}</td>
                    <td className="px-3 py-2 text-end tabular-nums">{s.failed}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div>
          <p className="mb-1 font-medium">{t('admin.automations.ordersTitle')}</p>
          {!r.orders.length ? (
            <p className="text-ink-2">{t('admin.automations.noOrders')}</p>
          ) : (
            <ul className="space-y-0.5 text-ink-2">
              {r.orders.map((o) => (
                <li key={o.currency}>
                  {t('admin.campaigns.ordersLine', {
                    orders: o.orders,
                    paid: o.paidOrders,
                    revenue: money(o.revenueMinor, o.currency),
                  })}
                </li>
              ))}
            </ul>
          )}
          <p className="mt-1 text-ink-3">{t('admin.automations.ordersNote')}</p>
        </div>
      </div>
    </Section>
  );
}
